import React, { useState, useEffect } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
// Debounce Hook (Inner)
const statusOptions = [
    { value: 'All', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'in_progress', label: 'In Progress' }
];

const AuditFilters = ({ onApply, loading }) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [status, setStatus] = useState('All');
    const [search, setSearch] = useState('');
    const [activeAction, setActiveAction] = useState(null);

    // Internal debounced value for search
    const [debouncedSearch, setDebouncedValue] = useState(search);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(search);
        }, 500);
        return () => clearTimeout(handler);
    }, [search]);

    // Effect: Trigger apply when debounced search changes
    useEffect(() => {
        handleApply();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    // Reset activeAction when loading finishes
    useEffect(() => {
        if (!loading) {
            setActiveAction(null);
        }
    }, [loading]);

    const handleApply = (manual = false) => {
        if (manual) setActiveAction('apply');
        onApply({
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
            status,
            search: debouncedSearch
        });
    };

    const handleReset = () => {
        setActiveAction('reset');
        setStartDate(null);
        setEndDate(null);
        setStatus('All');
        setSearch('');
        // We can force an immediate apply here for reset UI responsiveness
        onApply({
            startDate: null,
            endDate: null,
            status: 'All',
            search: ''
        });
    };

    return (
        <div className="aii-filter-container">
            <div className="aii-filter-group" style={{ flexGrow: 2 }}>
                <label className="aii-filter-label">Search Prompt or User ID</label>
                <input
                    type="text"
                    className="aii-input"
                    placeholder="Type Prompt or User ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="aii-filter-group" style={{ minWidth: '200px' }}>
                <label className="aii-filter-label">Status</label>
                <Select
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === status)}
                    onChange={(opt) => {
                        const newVal = opt ? opt.value : 'All';
                        setStatus(newVal);
                        // Optional: Trigger apply immediately on status change? User requirement says "Filter change resets pagination".
                        // Usually improved UX to trigger immediately for dropdowns
                        // We will let the user click "APPLY" or we can trigger it. 
                        // The existing code only triggered on search debounce or manual button.
                        // Let's stick to the manual "APPLY" button for strict correctness unless requested otherwise, 
                        // BUT user said "Filter change resets pagination... First batch loads immediately".
                        // This implies immediate reaction. "Changing filter must NOT block UI".
                        // Let's keep it manual for now to match the "APPLY" button presence, OR trigger it.
                        // Actually, looking at the previous code, handleApply was called on button click.
                        // I will keep the explicit Apply button as primary trigger.
                    }}
                    classNamePrefix="aii-select"
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: '42px',
                            borderColor: '#cbd5e1',
                            borderRadius: '6px'
                        })
                    }}
                />
            </div>

            <div className="aii-filter-group">
                <label className="aii-filter-label">Start Date</label>
                <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    className="aii-input"
                    placeholderText="Select Start Date"
                    isClearable
                    dateFormat="yyyy-MM-dd"
                    wrapperClassName="w-100"
                />
            </div>

            <div className="aii-filter-group">
                <label className="aii-filter-label">End Date</label>
                <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    className="aii-input"
                    placeholderText="Select End Date"
                    isClearable
                    dateFormat="yyyy-MM-dd"
                    wrapperClassName="w-100"
                />
            </div>

            <div className="aii-filter-group" style={{ display: 'flex', gap: '8px' }}>
                <button
                    className="aii-btn aii-btn-primary"
                    style={{ flex: 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    onClick={() => handleApply(true)}
                    disabled={loading}
                >
                    {loading && activeAction === 'apply' ? (
                        <div className="aii-wave-loader">
                            <span className="aii-wave-dot"></span>
                            <span className="aii-wave-dot"></span>
                            <span className="aii-wave-dot"></span>
                        </div>
                    ) : 'APPLY'}
                </button>
                <button
                    className="aii-btn aii-btn-secondary"
                    style={{ flex: 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    onClick={handleReset}
                    disabled={loading}
                >
                    {loading && activeAction === 'reset' ? (
                        <div className="aii-wave-loader">
                            <span className="aii-wave-dot"></span>
                            <span className="aii-wave-dot"></span>
                            <span className="aii-wave-dot"></span>
                        </div>
                    ) : 'RESET'}
                </button>
            </div>
        </div>
    );
};

export default AuditFilters;

